import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  UiPaletteStep,
  UiTemplateItem,
} from "@/features/template-builder/queries";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import { useSession } from "@/platform/auth";
import { useTenant } from "@/domains/tenant/hooks";
import { useInsertStepAtPosition } from "./use-insert-step-at-position";
import { insertCachedTemplateStep } from "@/features/template-builder/queries/cache";

export type PaletteTemplateGroup = {
  template: UiTemplateItem;
  steps: UiPaletteStep[];
};

const BLANK_SECTION_TEMPLATE_ID = "__blank_section_template__";
const BLANK_SECTION_STEP_ID = "__blank_section_step__";
const BLANK_SECTION_STEP_KEY = `${BLANK_SECTION_TEMPLATE_ID}:${BLANK_SECTION_STEP_ID}`;

type UseSectionPaletteParams = {
  analysisId: string;
  stepsCount: number;
};

export function useSectionPalette({
  analysisId,
  stepsCount,
}: UseSectionPaletteParams) {
  const { user } = useSession();
  const { data: tenantData } = useTenant();
  const queryClient = useQueryClient();
  const { mutateAsync: insertAtPosition, isPending: isInsertingAtPosition } =
    useInsertStepAtPosition();

  const { data: targetTemplateSteps } = useQuery(
    templateBuilderQueries.steps(analysisId),
  );

  const { mutateAsync: insertTopMutation, isPending: isInsertingTop } =
    useMutation({
      mutationFn: async ({
        templateId,
        templateStepId,
      }: {
        templateId: string;
        templateStepId?: string;
      }) =>
        templateBuilderMutations.createTemplateStepTop({
          templateId,
          templateStepId,
        }),
      onSuccess: (step, { templateId }) => {
        insertCachedTemplateStep(queryClient, templateId, step);
      },
    });

  const { mutateAsync: insertBottomMutation, isPending: isInsertingBottom } =
    useMutation({
      mutationFn: async ({
        templateId,
        templateStepId,
      }: {
        templateId: string;
        templateStepId?: string;
      }) =>
        templateBuilderMutations.createTemplateStepBottom({
          templateId,
          templateStepId,
        }),
      onSuccess: (step, { templateId }) => {
        insertCachedTemplateStep(queryClient, templateId, step);
      },
    });

  const [search, setSearch] = useState("");
  const [showGlobal, setShowGlobal] = useState(true);
  const [openTemplates, setOpenTemplates] = useState<Record<string, boolean>>(
    {},
  );
  const [selectedStepKey, setSelectedStepKey] = useState<string | null>(null);

  const { data: templates, isLoading: isLoadingTemplates } = useQuery(
    templateBuilderQueries.list(
      { search: "", showGlobal },
      tenantData?.tenant.tenant_id,
    ),
  );

  const orderedTemplates = useMemo((): UiTemplateItem[] => {
    const otherTemplates = (templates ?? []).filter((t) => t.id !== analysisId);
    return [
      ...otherTemplates.filter((t) => !t.isGlobal),
      ...otherTemplates.filter((t) => t.isGlobal),
    ];
  }, [templates, analysisId]);

  const templateIds = useMemo(
    () => orderedTemplates.map((t) => t.id),
    [orderedTemplates],
  );

  const { data: paletteSteps, isLoading: isLoadingSteps } = useQuery(
    templateBuilderQueries.paletteSteps(templateIds),
  );

  const templateById = useMemo(
    () =>
      new Map<string, UiTemplateItem>(orderedTemplates.map((t) => [t.id, t])),
    [orderedTemplates],
  );

  const normalizedSearch = search.trim().toLowerCase();

  const filteredSteps = useMemo(() => {
    const steps = paletteSteps ?? [];
    if (!normalizedSearch) return steps;

    return steps.filter((s) => {
      const tpl = templateById.get(s.templateId);
      const haystack = `${s.name} ${tpl?.name ?? ""}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [paletteSteps, normalizedSearch, templateById]);

  const grouped = useMemo((): PaletteTemplateGroup[] => {
    const byTemplate = new Map<string, UiPaletteStep[]>();
    for (const step of filteredSteps) {
      const next = byTemplate.get(step.templateId) ?? [];
      next.push(step);
      byTemplate.set(step.templateId, next);
    }

    return orderedTemplates
      .map((t) => ({
        template: t,
        steps: (byTemplate.get(t.id) ?? [])
          .slice()
          .sort((a, b) => a.order - b.order),
      }))
      .filter((g) => g.steps.length > 0);
  }, [filteredSteps, orderedTemplates]);

  const selectedStep = useMemo(() => {
    if (!selectedStepKey) return null;
    if (selectedStepKey === BLANK_SECTION_STEP_KEY) {
      return {
        templateId: BLANK_SECTION_TEMPLATE_ID,
        id: BLANK_SECTION_STEP_ID,
        name: "New Section",
        order: 0,
        description: "",
        outputTypes: ["bulletpoints"],
      } satisfies UiPaletteStep;
    }

    const [templateId, stepId] = selectedStepKey.split(":");
    const step = (paletteSteps ?? []).find(
      (s) => s.templateId === templateId && s.id === stepId,
    );
    if (!step) return null;
    return step;
  }, [selectedStepKey, paletteSteps]);

  const orderedCurrentStepOrders = useMemo(
    () =>
      (targetTemplateSteps ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((step) => step.order),
    [targetTemplateSteps],
  );

  const resolveInsertOrderForPosition = (position: number): number => {
    const normalizedPosition = Math.max(1, Math.floor(position));
    if (orderedCurrentStepOrders.length === 0) {
      return normalizedPosition;
    }

    if (normalizedPosition <= orderedCurrentStepOrders.length) {
      return orderedCurrentStepOrders[normalizedPosition - 1]!;
    }

    return orderedCurrentStepOrders[orderedCurrentStepOrders.length - 1]! + 1;
  };

  const resolveTemplateStepId = (step: UiPaletteStep): string | undefined => {
    const isBlankSection =
      step.templateId === BLANK_SECTION_TEMPLATE_ID ||
      step.id === BLANK_SECTION_STEP_ID;
    return isBlankSection ? undefined : step.id;
  };

  const handleInsert = (step: UiPaletteStep, position: number) => {
    if (!analysisId || !user || !tenantData) return Promise.resolve();

    return insertAtPosition({
      templateId: analysisId,
      position,
      templateStepId: resolveTemplateStepId(step),
    });
  };

  const handleInsertTop = (step: UiPaletteStep) => {
    if (!analysisId || !user || !tenantData) return Promise.resolve();
    return insertTopMutation({
      templateId: analysisId,
      templateStepId: resolveTemplateStepId(step),
    });
  };

  const handleInsertBottom = (step: UiPaletteStep) => {
    if (!analysisId || !user || !tenantData) return Promise.resolve();
    return insertBottomMutation({
      templateId: analysisId,
      templateStepId: resolveTemplateStepId(step),
    });
  };

  const isInserting =
    isInsertingAtPosition || isInsertingTop || isInsertingBottom;

  const buttonDisabled = isInserting || !user || !tenantData;
  const isLoading = isLoadingTemplates || isLoadingSteps;
  const hasResults = grouped.length > 0;
  const insertDisabled = buttonDisabled || !selectedStep;

  const insertTop = () => {
    return selectedStep ? handleInsertTop(selectedStep) : Promise.resolve();
  };

  const insertBottom = () => {
    return selectedStep ? handleInsertBottom(selectedStep) : Promise.resolve();
  };

  const insertAt = (position: number) => {
    if (selectedStep) {
      return handleInsert(
        selectedStep,
        resolveInsertOrderForPosition(position),
      );
    }
    return Promise.resolve();
  };

  const clearSelection = () => setSelectedStepKey(null);

  return {
    search,
    setSearch,
    showGlobal,
    setShowGlobal,
    grouped,
    isLoading,
    hasResults,
    openTemplates,
    setOpenTemplates,
    selectedStepKey,
    setSelectedStepKey,
    insertTop,
    insertBottom,
    insertAt,
    insertDisabled,
    isInserting,
    clearSelection,
    selectedStepName: selectedStep?.name ?? "section",
    stepsCount,
    blankSectionStepKey: BLANK_SECTION_STEP_KEY,
  };
}
